import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../../store';
import {
    urlDynamicActionsReply,
    urlDynamicActionsDocuments,
    urlDynamicActionsFormdesigner,
} from '../../../Constants';
import {
    AdditionalInputTypeEnum,
    IAdditionalInput,
    IAdditionalInputResponseData,
    isAdditionalInputResponseData,
    standardizeAdditionalInputResponse,
} from '../sliceProjectWorkflow';
import { GlobalVariables } from '../../../GlobalVariables';
import { resetUtilsFileUploadState, uploadFiles } from '../../general/sliceFileUpload';
import { resetFileState as resetFileUploadState } from '../../import/sliceImportFileUpload';
import { IAdditionalInfoResultButtonEnum } from '../../../components/General/Dialogs/_parts/AdditionalInputFooterActions';
import { IActionCallbackArguments } from '../sliceProjectActions';
import { dynamicFormName } from '@eplass/formdesigner/domain';
import { apiFetch } from '../../../util/ProjectUtils';

let setValueLocal = undefined;

const setFormValue = (res: object) => {
    if (!setValueLocal) {
        const error = new Error('No setValue');
        console.error(error);
        throw error;
    }
    if (isAdditionalInputResponseData(res)) {
        return;
    }
    if (Object.keys(res).length === 0) {
        return;
    }
    Object.keys(res).map(formFieldName =>
        setValueLocal(`${dynamicFormName}.0.${formFieldName}`, res?.[formFieldName], {
            shouldDirty: true,
        }),
    );
};

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsDynamicActionState = {
    isDialogOpen: false,
    isFormdesignersDynamicActionDialog: false,
    additionalInput: null,
    loading: false,
    loadingRunDynamicAction: false,
    loadingFormDesignerDynamicAction: false,
    error: null,
    data: null,
};

const reducers = {
    closeDynamicActionDialog(state: IProjectDocumentsDynamicActionState) {
        state.isDialogOpen = false;
        state.isFormdesignersDynamicActionDialog = false;
    },

    resetErrorDynamicAction(state: IProjectDocumentsDynamicActionState) {
        state.error = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Run Dynamic Action for Formdesigner
     */
    runFormdesignersDynamicAction: async (
        config: IActionCallbackArguments,
    ): Promise<object | IAdditionalInputResponseData> => {
        try {
            const { universalId, replicaId, actionKey, jsonSchema, changedFormValues, setValue } =
                config;
            setValueLocal = setValue;

            const formData = new FormData();
            formData.append('replicaId', replicaId);
            formData.append('universalId', universalId);
            formData.append('actionKey', actionKey);
            formData.append('documentData', JSON.stringify(changedFormValues));
            formData.append('schemaJson', jsonSchema);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const response = await apiFetch(urlDynamicActionsFormdesigner, request);

            if (response.ok) {
                const res = await response.json();
                setFormValue(res);
                return res;
            } else {
                return Promise.reject(new Error(await response.text()));
            }
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },
    /**
     * Run Dynamic Action
     */
    runDynamicAction: async (
        config: IActionCallbackArguments,
    ): Promise<object | IAdditionalInputResponseData> => {
        try {
            const { replicaId, actionKey, documentIds, universalId } = config;

            const universalIds = [universalId ?? documentIds].flat();

            const formData = new FormData();
            formData.append('replicaId', replicaId);
            formData.append('universalIds', universalIds.join('|'));
            formData.append('actionKey', actionKey);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const response = await apiFetch(urlDynamicActionsDocuments, request);

            if (response.ok) {
                return await response.json();
            } else {
                return Promise.reject(new Error(await response.text()));
            }
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * On Dynamic Action Task Reply
     */
    onDynamicActionTaskReply: async (
        { dialogResultButton, additionalInputState, replicaId }: IDynamicActionReplyArguments,
        thunkApi,
    ): Promise<IAdditionalInputResponseData | object> => {
        try {
            let state = thunkApi.getState();
            const { taskId } = state.projectDocumentsDynamicSelection.additionalInput;
            const { dialogType } = state.projectDocumentsDynamicSelection.additionalInput.response;
            const isFormdesignersDynamicActionDialog =
                state.projectDocumentsDynamicSelection.isFormdesignersDynamicActionDialog;

            let resultValues;

            if (
                dialogType === AdditionalInputTypeEnum.UploadFiles &&
                [IAdditionalInfoResultButtonEnum.Yes, IAdditionalInfoResultButtonEnum.Ok].includes(
                    dialogResultButton,
                )
            ) {
                const filesArray = GlobalVariables.getAllFiles();

                if (filesArray.length) {
                    await thunkApi.dispatch(uploadFiles(filesArray));
                }

                state = thunkApi.getState();

                const { filesPathes } = state.fileUpload;
                resultValues = filesPathes;
            } else {
                resultValues = additionalInputState;
            }

            const content = {
                dialogResultButton: dialogResultButton,
                dialogResultValues: resultValues,
            };
            const formData = new FormData();
            formData.append('replicaId', replicaId);
            formData.append('taskId', taskId);
            formData.append('content', JSON.stringify(content));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(urlDynamicActionsReply, request);

            thunkApi.dispatch(resetFileUploadState());
            thunkApi.dispatch(resetUtilsFileUploadState());
            GlobalVariables.resetFiles();

            if (response.ok) {
                const res = await response.json();
                isFormdesignersDynamicActionDialog && setFormValue(res);

                return res;
            } else {
                return Promise.reject(new Error(response.status.toString()));
            }
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsDynamicActionState>) => {
        /**
         * Reducers for the 'runFormdesignersDynamicAction' thunk.
         */
        builder.addCase(
            runFormdesignersDynamicAction.pending,
            (state: Draft<IProjectDocumentsDynamicActionState>) => {
                state.loadingFormDesignerDynamicAction = true;
                state.loadingRunDynamicAction = true;
                state.error = null;
            },
        );
        builder.addCase(
            runFormdesignersDynamicAction.fulfilled,
            (
                state: Draft<IProjectDocumentsDynamicActionState>,
                action: IAction<object | IAdditionalInputResponseData>,
            ) => {
                state.loadingFormDesignerDynamicAction = false;
                state.loadingRunDynamicAction = false;
                state.error = null;
                state.additionalInput = null;

                if (!isAdditionalInputResponseData(action.payload)) {
                    return;
                }
                state.isDialogOpen = true;
                state.isFormdesignersDynamicActionDialog = true;
                state.additionalInput = {
                    ...action.payload,
                    response: standardizeAdditionalInputResponse(action.payload.response),
                };
            },
        );
        builder.addCase(
            runFormdesignersDynamicAction.rejected,
            (
                state: Draft<IProjectDocumentsDynamicActionState>,
                action: IAction<SerializedError>,
            ) => {
                state.loadingFormDesignerDynamicAction = false;
                state.loadingRunDynamicAction = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'runDynamicAction' thunk.
         */
        builder.addCase(
            runDynamicAction.pending,
            (state: Draft<IProjectDocumentsDynamicActionState>) => {
                state.loadingRunDynamicAction = true;
                state.error = null;
            },
        );
        builder.addCase(
            runDynamicAction.fulfilled,
            (
                state: Draft<IProjectDocumentsDynamicActionState>,
                action: IAction<object | IAdditionalInputResponseData>,
            ) => {
                state.loadingRunDynamicAction = false;
                state.error = null;
                state.additionalInput = null;
                state.data = action.payload;

                if (!isAdditionalInputResponseData(action.payload)) {
                    return;
                }
                state.isDialogOpen = true;
                state.additionalInput = {
                    ...action.payload,
                    response: standardizeAdditionalInputResponse(action.payload.response),
                };
            },
        );
        builder.addCase(
            runDynamicAction.rejected,
            (
                state: Draft<IProjectDocumentsDynamicActionState>,
                action: IAction<SerializedError>,
            ) => {
                state.loadingRunDynamicAction = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onDynamicActionTaskReply' thunk.
         */
        builder.addCase(
            onDynamicActionTaskReply.pending,
            (state: Draft<IProjectDocumentsDynamicActionState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            onDynamicActionTaskReply.fulfilled,
            (
                state: Draft<IProjectDocumentsDynamicActionState>,
                action: IAction<IAdditionalInputResponseData | object>,
            ) => {
                state.loading = false;
                state.additionalInput = null;

                if (isAdditionalInputResponseData(action.payload)) {
                    state.additionalInput = {
                        ...action.payload,
                        response: standardizeAdditionalInputResponse(action.payload.response),
                    };
                } else {
                    state.data = action.payload;
                    state.isDialogOpen = false;
                    state.isFormdesignersDynamicActionDialog = false;
                }
            },
        );
        builder.addCase(
            onDynamicActionTaskReply.rejected,
            (
                state: Draft<IProjectDocumentsDynamicActionState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const runFormdesignersDynamicAction = createAsyncThunk(
    urlDynamicActionsFormdesigner,
    thunks.runFormdesignersDynamicAction,
);

export const runDynamicAction = createAsyncThunk(
    urlDynamicActionsDocuments,
    thunks.runDynamicAction,
);

export const onDynamicActionTaskReply = createAsyncThunk(
    urlDynamicActionsReply,
    thunks.onDynamicActionTaskReply,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectDocumentsDynamicActionState extends SlicePropsWithData<any> {
    isDialogOpen: boolean;
    additionalInput: IAdditionalInput;
    loadingRunDynamicAction: boolean;
    loadingFormDesignerDynamicAction: boolean;
    isFormdesignersDynamicActionDialog: boolean;
}

/**
 * Create the redux slice.
 */
export const projectDocumentsDynamicActionSlice = createSlice({
    name: 'projectDocumentsDynamicAction',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export interface IDynamicActionReplyArguments {
    dialogResultButton: IAdditionalInfoResultButtonEnum;
    additionalInputState: string[];
    replicaId: string;
}

/**
 * Export functions from slice actions.
 */
export const { resetErrorDynamicAction } = projectDocumentsDynamicActionSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsDynamicActionSlice.reducer;
